import { useState, useMemo, useEffect } from 'react';
import {
  Loader2,
  RefreshCw,
  Send,
  Mail,
  FileText,
  History,
  ImageIcon,
  Clipboard,
  Repeat,
  Users,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import type { LegacyOrder } from '@/api/legacy';
import { legacyApi } from '@/api/legacy-client';
import { parseHtmlToReact } from '@/lib/html-parser';
import {
  useRefreshSchet,
  useAddOrderComment,
  useSendGuarantee,
} from '@/features/legacy/hooks/useOrders';
import { LinkText } from '@/components/LinkText';
import { toast } from 'sonner';

interface OrderDetailSheetProps {
  order: LegacyOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReorder?: (name: string, company: string) => void;
  onReorderOther?: (name: string) => void;
}

interface LogEntry {
  header: string;
  content: string;
}

/**
 * Extract photo file paths from order log
 */
function extractPhotoPaths(log: string): string[] {
  if (!log) return [];

  const matches = log.match(/href='([^']+\.jpg)'/gi);
  if (!matches) return [];

  const photos = matches.map((m) => {
    const match = m.match(/href='([^']+\.jpg)'/i);
    if (!match) return '';
    let filename = match[1];

    // Extract just the file path
    if (filename.startsWith('/files.php?f=')) {
      return filename.replace('/files.php?f=', '');
    } else {
      return filename.replace(/^\/+/, '');
    }
  }).filter(Boolean);

  // Remove duplicates and sort
  return [...new Set(photos)].sort();
}

/**
 * Component to display an authenticated image from legacy API
 */
function AuthImage({ filePath, alt }: { filePath: string; alt: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchImage() {
      try {
        setLoading(true);
        setError(false);
        const response = await legacyApi.get(`/api_files.php?f=${encodeURIComponent(filePath)}`, {
          responseType: 'blob',
        });
        if (!cancelled) {
          const url = URL.createObjectURL(response.data);
          setBlobUrl(url);
        }
      } catch {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchImage();

    return () => {
      cancelled = true;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [filePath]);

  if (loading) {
    return (
      <div className="w-20 h-20 rounded border bg-muted flex items-center justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div className="w-20 h-20 rounded border bg-muted flex items-center justify-center">
        <ImageIcon className="w-4 h-4 text-muted-foreground" />
      </div>
    );
  }

  return (
    <a href={blobUrl} target="_blank" rel="noopener noreferrer">
      <img
        src={blobUrl}
        alt={alt}
        className="w-20 h-20 object-cover rounded border hover:opacity-80 transition-opacity"
      />
    </a>
  );
}

/**
 * Parse order log into structured entries
 */
function parseLog(log: string): LogEntry[] {
  if (!log) return [];

  const parts = log.split('----- ');
  const entries: LogEntry[] = [];

  for (let i = parts.length - 1; i >= 0; i--) {
    if (parts[i].includes('Создание') || parts[i].includes('Изменение')) {
      const header = parts[i].trim();
      const content = parts[i + 1] || '';
      entries.push({ header, content: content.trim() });
    }
  }

  return entries;
}

export function OrderDetailSheet({
  order,
  open,
  onOpenChange,
  onReorder,
  onReorderOther,
}: OrderDetailSheetProps) {
  const [comment, setComment] = useState('');
  const [email, setEmail] = useState('');

  const refreshSchet = useRefreshSchet();
  const addComment = useAddOrderComment();
  const sendGuarantee = useSendGuarantee();

  const photoPaths = useMemo(() => (order ? extractPhotoPaths(order.log) : []), [order?.log]);
  const logEntries = useMemo(() => (order ? parseLog(order.log) : []), [order?.log]);
  const hasDoki = order && order.doki && order.doki.length > 5;
  const isArchived = order?.varhive === '1';

  const handleRefreshSchet = () => {
    if (!order) return;
    refreshSchet.mutate(Number(order.id), {
      onSuccess: () => onOpenChange(false),
    });
  };

  const handleSendComment = () => {
    if (!order || !comment.trim()) return;
    addComment.mutate(
      { id: Number(order.id), comment: comment.trim() },
      {
        onSuccess: () => {
          setComment('');
          onOpenChange(false);
        },
      }
    );
  };

  const handleSendGuarantee = () => {
    if (!order || !email.trim()) return;
    sendGuarantee.mutate(
      { id: Number(order.id), email: email.trim() },
      {
        onSuccess: () => {
          setEmail('');
          onOpenChange(false);
        },
      }
    );
  };

  if (!order) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-start gap-2">
            <FileText className="h-5 w-5 shrink-0 mt-0.5" />
            <span className="break-words min-w-0">{order.name}</span>
            <button
              className="shrink-0 p-1 mt-1 rounded hover:bg-muted transition-colors"
              onClick={() => {
                const text = [order.name, order.comment].filter(Boolean).join('\n');
                navigator.clipboard.writeText(text);
                toast.success('Скопировано');
              }}
            >
              <Clipboard className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            {isArchived && (
              <Badge variant="secondary" className="text-xs shrink-0">
                Архив
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            {order.company} • {new Date(order.created).toLocaleDateString('ru-RU')}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Comment section */}
          {order.comment && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm">
                <LinkText>{order.comment}</LinkText>
              </p>
            </div>
          )}

          {/* Invoice info */}
          {hasDoki && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-800">Накладная</p>
              <p className="text-xs text-blue-600 mt-1">
                <LinkText>{order.doki}</LinkText>
              </p>
            </div>
          )}

          {/* Reorder actions */}
          {(onReorder || onReorderOther) && (
            <div className="flex gap-2">
              {onReorder && (
                <Button
                  variant="outline"
                  className="flex-1 h-11"
                  onClick={() => onReorder(order.name, order.company)}
                >
                  <Repeat className="h-4 w-4 mr-2" />
                  Повторить
                </Button>
              )}
              {onReorderOther && (
                <Button
                  variant="outline"
                  className="flex-1 h-11"
                  onClick={() => onReorderOther(order.name)}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Другому
                </Button>
              )}
            </div>
          )}

          {/* Photos */}
          {photoPaths.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Фото счетов ({photoPaths.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {photoPaths.map((filePath, idx) => (
                  <AuthImage
                    key={filePath}
                    filePath={filePath}
                    alt={`Счёт ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3 pt-2">
            {/* Refresh invoice - only for active orders */}
            {!isArchived && (
              <Button
                variant="outline"
                className="w-full justify-start bg-green-50 hover:bg-green-100 border-green-300 dark:bg-green-900/30 dark:hover:bg-green-900/50 dark:border-green-700"
                onClick={handleRefreshSchet}
                disabled={refreshSchet.isPending}
              >
                {refreshSchet.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Попросить освежить счёт
              </Button>
            )}

            {/* Send comment - available for all orders */}
            <div className="space-y-2">
              <Input
                placeholder="Уточнение по заявке..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={addComment.isPending}
              />
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleSendComment}
                disabled={!comment.trim() || addComment.isPending}
              >
                {addComment.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Отправить уточнение
              </Button>
            </div>

            {/* Send warranty - available if has invoice */}
            {hasDoki && (
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="E-mail для гарантийного талона..."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={sendGuarantee.isPending}
                />
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={handleSendGuarantee}
                  disabled={!email.trim() || sendGuarantee.isPending}
                >
                  {sendGuarantee.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Выслать гарантийку
                </Button>
              </div>
            )}
          </div>

          {/* Order history */}
          {logEntries.length > 0 && (
            <div className="pt-2">
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <History className="h-4 w-4" />
                История
              </h4>
              <div className="space-y-2">
                {logEntries.map((entry, idx) => (
                  <div key={idx} className="border rounded-lg overflow-hidden">
                    <div className="bg-muted px-3 py-1.5">
                      <p className="text-xs font-medium">{entry.header}</p>
                    </div>
                    <div className="p-3">
                      <p className="text-xs text-muted-foreground whitespace-pre-wrap break-words">
                        {parseHtmlToReact(entry.content.replace(/\n/g, '<br />'))}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
